import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { WorkflowService, sharedApiClient } from '../../fetcher';
import { Table, createTextColumn, createEnumColumn, createCustomColumn, useTableState, StatusBadge } from '@medprompt/shared';
import '@medprompt/shared/src/common/modals.css';
import '../WorkflowView.css';

const WorkflowLogsModal = ({ isOpen, onClose, documentId, onWorkflowClick }) => {
  const navigate = useNavigate();
  const workflowManager = useMemo(() => new WorkflowService(sharedApiClient), []);

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState
  } = useTableState('DocumentWorkflowLogsTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'desc' }
  });

  // Fetch workflow logs for document
  useEffect(() => {
    if (!isOpen || !documentId) return;

    const fetchLogs = async () => {
      try {
        setLoading(true);
        const data = await workflowManager.fetchWorkflowLogsForDocument(documentId);
        setLogs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch workflow logs for document:', error);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [isOpen, documentId, workflowManager]);

  // Table columns
  const columns = useMemo(() => [
    createCustomColumn(
      'timestamp',
      'Timestamp',
      (log) => log.timestamp,
      (value) => {
        if (!value) return '—';
        try {
          return new Date(value).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
        } catch {
          return value;
        }
      },
      {
        sortable: true,
        filterable: false,
        compareFn: (a, b) => {
          const va = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const vb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return va - vb;
        }
      }
    ),

    createEnumColumn(
      'logLevel',
      'Level',
      (log) => log.logLevel,
      ['INFO', 'ERROR'],
      {
        renderer: (value) => (
          <StatusBadge
            label={value}
            colorFamily={value === 'ERROR' ? 'red' : 'blue'}
            size="normal"
          />
        ),
        sortable: true
      }
    ),

    createTextColumn('message', 'Message', (log) => log.message)
  ], []);

  const handleLogClick = (log) => {
    if (log.ruleId) {
      // If onWorkflowClick is provided (from DocumentDetailViewController), use it
      // Otherwise navigate directly (legacy behavior)
      if (onWorkflowClick) {
        onWorkflowClick(log.ruleId);
      } else {
        onClose();
        navigate(`/workflow/${log.ruleId}`, {
          state: { 
            documentId: documentId,
            ruleId: log.ruleId 
          }
        });
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 1001 }}>
      <div className="modal-container workflow-logs-modal" style={{ 
        maxWidth: '1200px', 
        maxHeight: '90vh',
        width: '90vw'
      }}>
        <div className="modal-header">
          <h2>Workflow Runs</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-content" style={{ minHeight: '400px' }}>
          {loading ? (
            <div className="loading-container" style={{ padding: '60px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
              <p>Loading workflow logs...</p>
            </div>
          ) : (
            <Table
              id="DocumentWorkflowLogsTable"
              data={logs}
              columns={columns}
              getRowKey={(log, index) => `${log.timestamp}-${log.ruleId}-${index}`}
              sortState={sortState}
              onSortChange={setSortState}
              filterValues={filterValues}
              onFilterChange={setFilterValues}
              disabledFilters={disabledFilters}
              onDisabledFiltersChange={setDisabledFilters}
              paginationState={paginationState}
              onPaginationChange={setPaginationState}
              onRowClick={handleLogClick}
              emptyMessage="No workflow runs found for this document."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowLogsModal;
